/**
 * useVirtualAccountData Hook
 * 가상계좌 데이터 관리 훅
 *
 * @description
 * - 가상계좌 목록 조회 (페이징 지원)
 * - 필터 기반 검색 기능
 * - 배열/객체 응답 형식 모두 처리
 */
import { useState, useCallback } from 'react';
import { virtualAccountApi } from '../api/virtualAccountApi';
import { VirtualAccount, VirtualAccountFilters } from '../types/virtualAccount.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';

/**
 * 가상계좌 데이터 관리 훅
 * @returns {Object} 가상계좌 관련 상태 및 함수
 * @property {VirtualAccount[]} accounts - 가상계좌 목록
 * @property {boolean} loading - 로딩 상태
 * @property {number} totalElements - 전체 항목 수
 * @property {number} totalPages - 전체 페이지 수
 * @property {Function} fetchAccounts - 가상계좌 목록 조회 함수
 */
export const useVirtualAccountData = () => {
    const [accounts, setAccounts] = useState<VirtualAccount[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const { showToast } = useToast();

    /**
     * 가상계좌 목록 조회
     * @param {VirtualAccountFilters} filters - 검색 필터
     * @param {number} page - 페이지 번호 (0부터 시작)
     * @param {number} size - 페이지당 항목 수
     */
    const fetchAccounts = useCallback(async (
        filters: VirtualAccountFilters,
        page = 0,
        size = 10
    ) => {
        setLoading(true);
        try {
            const response = await virtualAccountApi.getVirtualAccounts(filters, page, size);

            // 응답이 배열인 경우와 객체인 경우 모두 처리
            if (Array.isArray(response)) {
                setAccounts(response);
                setTotalElements(response.length);
                setTotalPages(Math.ceil(response.length / size));
            } else if (response.content) {
                setAccounts(response.content);
                setTotalElements(response.totalElements);
                setTotalPages(response.totalPages);
            } else {
                setAccounts([]);
                setTotalElements(0);
                setTotalPages(0);
            }
        } catch {
            showToast('가상계좌 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setAccounts([]);
            setTotalElements(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    return {
        accounts,
        loading,
        totalElements,
        totalPages,
        fetchAccounts,
    };
};
